/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import React from 'react';

import { defineMessages, FormattedMessage } from 'react-intl-next';

import { jsx } from '@atlaskit/css';
import {
	DefaultExtensionProvider,
	type ExtensionManifest,
} from '@atlaskit/editor-common/extensions';
import Lozenge from '@atlaskit/lozenge';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	createSkillNode,
	ROVO_SKILL_EXTENSION_KEY,
	ROVO_SKILL_EXTENSION_TYPE,
} from '@atlassian/conversation-assistant-content-transformer/utils/skills';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	defaultIcon,
	skillIcon,
	type SkillIconName,
} from '@atlassian/rovo-platform-ui-components/skills/skill-icons';
import { SkillTag } from '@atlassian/rovo-platform-ui-components/skills/SkillTag';
import { type Skill } from '@atlassian/rovo-platform-ui-components/skills/types';

const getSkillIcon = (iconName?: SkillIconName) =>
	iconName && skillIcon[iconName] ? skillIcon[iconName] : defaultIcon;

const skillsEditorMessages = defineMessages({
	betaLabel: {
		id: 'conversation-assistant-chat-prompt-input.skills-editor-extension.beta-label',
		defaultMessage: 'Beta',
		description: 'Beta badge label shown next to admin skill actions in the slash command menu',
	},
});

const adminSkillBetaLozenge = (
	<Lozenge appearance="discovery">
		<FormattedMessage {...skillsEditorMessages.betaLabel} />
	</Lozenge>
);

type UseSkillsManifestProps = {
	skills: Skill[];
	isConversationAgentic: boolean;
	promptHasSkill: boolean;
	enableSkills?: boolean;
};

export const useSkillsManifest = ({
	skills,
	isConversationAgentic,
	promptHasSkill,
	enableSkills = true,
}: UseSkillsManifestProps): DefaultExtensionProvider<Skill>[] | undefined => {
	const { sendAnalyticsEvent } = useAnalytics();

	const createManifest = React.useCallback(
		(skills: Skill[]) => {
			const manifest: ExtensionManifest<Skill> = {
				title: 'Skills',
				type: ROVO_SKILL_EXTENSION_TYPE,
				key: ROVO_SKILL_EXTENSION_KEY,
				categories: ['Skills'],
				icons: {
					'16': defaultIcon,
					'24': defaultIcon,
					'48': defaultIcon,
				},
				modules: {
					quickInsert: skills.map((skill) => {
						const { slug, description, name, product, iconName, tags } = skill;
						const lozenge =
							tags?.some((t) => t.toLowerCase() === 'beta') &&
							fg('rovo_chat_editor_admin_skill_beta_lozenge_enabled')
								? adminSkillBetaLozenge
								: undefined;
						return {
							key: slug,
							keywords: ['skills', 'skill', slug, product ?? ''],
							// 1 is the default priority
							priority: 99,
							title: name,
							description: description,
							icon: getSkillIcon(iconName),
							lozenge,
							action: async () => {
								sendAnalyticsEvent({
									eventType: 'ui',
									action: 'clicked',
									actionSubject: 'button',
									actionSubjectId: 'rovoChatSkillSelected',
									attributes: {
										slashCommand: slug,
										skillProductKey: product,
										skillSelectionSource: 'editorTypeahead',
									},
								});
								return createSkillNode(skill);
							},
						};
					}),
					nodes: {
						skillTag: {
							type: 'inlineExtension',
							render:
								async () =>
								({ node }) => {
									const params = node.parameters;
									if (params) {
										return <SkillTag {...params} />;
									}
									return null;
								},
						},
					},
				},
			};

			return manifest;
		},
		[sendAnalyticsEvent],
	);

	const manifestProvider = React.useMemo(() => {
		// Return undefined if skills are disabled
		if (!enableSkills) {
			return undefined;
		}
		// skills are only applicable to non-agentic conversations
		// remove skills if a skill is already present in the prompt until chaining is allowed
		if ((isConversationAgentic || promptHasSkill) && fg('rovo_chat_enable_skills_ui_m1')) {
			return [new DefaultExtensionProvider([createManifest([])])];
		} else if (fg('rovo_chat_enable_skills_ui_m1')) {
			return [new DefaultExtensionProvider([createManifest(skills)])];
		}
		return undefined;
	}, [isConversationAgentic, promptHasSkill, createManifest, enableSkills, skills]);

	return manifestProvider;
};
